import React, { useState } from "react";
import { Box, Typography, Slider } from "@mui/material";

const EnergyLevel = ({ onResponseChange }) => {
  const [sliderValue, setSliderValue] = useState(2.5); 
  const [batteryLevel, setBatteryLevel] = useState(50);

  const handleEnergyChange = (event, newValue) => {
    setSliderValue(newValue);

    // Map the 0-5 slider range to a 0-100 battery percentage
    const mappedBatteryLevel = Math.round((newValue / 5) * 100);
    setBatteryLevel(mappedBatteryLevel); 

    // Categorize energy level based on thresholds
    let energyCategory;
    if (mappedBatteryLevel <= 20) energyCategory = "Very Low";
    else if (mappedBatteryLevel <= 40) energyCategory = "Low";
    else if (mappedBatteryLevel <= 60) energyCategory = "Moderate";
    else if (mappedBatteryLevel <= 80) energyCategory = "High";
    else energyCategory = "Very High";

    if (onResponseChange) {
      onResponseChange("energyLevel", energyCategory);
    }
  };

  // Determine the battery fill color based on its level
  const getFillColor = (level) => {
    if (level <= 20) return "#FF5B5B"; // Red for low energy
    if (level <= 40) return "#FFC300"; // Yellow for moderate energy
    if (level <= 60) return "#8BC34A"; // Light green for good energy
    if (level <= 80) return "#4CAF50"; // Dark green for high energy
    return "#388E3C"; // Deep green for full energy
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        minHeight: "60vh",
        padding: "2rem",
        boxSizing: "border-box",
        borderRadius: "12px",
        backgroundColor: "background.default",
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
      }}
    >
      {/* Left Section: Text */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "flex-start",
          paddingRight: "2rem",
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 600, marginBottom: "1rem", color: "#333" }}>
          Energy Level
        </Typography>
        <Typography variant="body1" sx={{ fontSize: "1.25rem", color: "#666" }}>
          How energetic do you feel right now?
        </Typography>
      </Box>

      {/* Center-Right Section: Slider and Battery */}
      <Box sx={{ flex: 2, display: "flex", flexDirection: "column", alignItems: "center" }}>
        {/* Row: Slider and Battery */}
        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center" }}>
          {/* Vertical Slider */}
          <Slider
            value={sliderValue}
            onChange={handleEnergyChange}
            step={0.1} // Smooth slider movement
            min={0}
            max={5}
            orientation="vertical"
            sx={{
              height: "200px",
              marginRight: "0.5rem",
              "& .MuiSlider-thumb": {
                width: "28px",
                height: "28px",
                backgroundColor: "white",
                border: "2px solid #4A90E2",
              },
              "& .MuiSlider-track": {
                backgroundColor: getFillColor(batteryLevel),
              },
              "& .MuiSlider-rail": {
                backgroundColor: "#E0E0E0",
              },
            }}
          />

          {/* Battery Icon */}
          <Box
            sx={{
              position: "relative",
              width: "80px",
              height: "200px",
              border: "4px solid #142850",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            {/* Battery Fill */}
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                width: "100%",
                height: `${batteryLevel}%`, // Adjust fill height (0 to 100%)
                backgroundColor: getFillColor(batteryLevel),
                transition: "height 0.3s ease, background-color 0.3s ease",
              }}
            />
          </Box>
        </Box>

        {/* Below Text */}
        <Typography variant="body2" sx={{ marginTop: "1rem", color: "#666", textAlign: "center" }}>
          Slide up to indicate your current energy levels
        </Typography>
      </Box>
    </Box>
  );
};

export default EnergyLevel;
